import { DATA_CHANNEL_LABEL } from '../../shared/constants.js';
import { formatBytes } from './format.js';
import { t } from './i18n/index.js';
import { fetchIceServers } from './ice.js';
import { startOffer, waitForChannelOpen, watchConnectionFailure, wireSignaling } from './peer.js';
import { formatRoute, watchRoute } from './route-stats.js';
import { sendFile, sendOptionsFor } from './send-file.js';
import type { SignalingClient } from './signaling-client.js';
import { createProgressPanel } from './ui/progress.js';
import type { ProgressPanel } from './ui/progress.js';

export type SendSessionHooks = Readonly<{
  /** The receiver's socket joined the room; WebRTC negotiation starts. */
  onReceiverConnected: () => void;
  /** The encrypted channel is open; the receiver is choosing where to save. */
  onWaitingForDestination: () => void;
  /** The receiver accepted; the page should mount the progress panel now. */
  onTransferStart: (progress: ProgressPanel) => void;
  onDelivered: (progress: ProgressPanel) => void;
}>;

/**
 * Sender half of a transfer once a room exists: waits for the counterpart, negotiates the peer
 * connection and streams the file. Shared by the link/PIN flow and by sending to a permanent inbox.
 */
export const runSendSession = async (
  file: File,
  signaling: SignalingClient,
  receiverJoined: Promise<unknown>,
  hooks: SendSessionHooks,
): Promise<void> => {
  const iceServers = await fetchIceServers();
  await receiverJoined;
  hooks.onReceiverConnected();

  const pc = new RTCPeerConnection({ iceServers });
  const unsubscribe = wireSignaling(pc, signaling);
  const failure = watchConnectionFailure(pc);
  const channel = pc.createDataChannel(DATA_CHANNEL_LABEL, { ordered: true });
  channel.binaryType = 'arraybuffer';
  await startOffer(pc, signaling);
  await Promise.race([waitForChannelOpen(channel), failure]);
  hooks.onWaitingForDestination();

  const progress = createProgressPanel(t('progress.sending'));
  let stopWatchingRoute: () => void = () => undefined;
  let startedAtMs = Date.now();
  await Promise.race([
    sendFile(channel, file, sendOptionsFor(pc), {
      onAccepted: () => {
        hooks.onTransferStart(progress);
        stopWatchingRoute = watchRoute(pc, (route) => progress.setRoute(formatRoute(route)));
        startedAtMs = Date.now();
      },
      onProgress: (update) =>
        progress.update({
          doneBytes: update.ackedBytes,
          totalBytes: update.totalBytes,
          startedAtMs,
          nowMs: Date.now(),
          detail: t('progress.sentDetail', { sent: formatBytes(update.sentBytes), acked: formatBytes(update.ackedBytes) }),
        }),
    }),
    failure,
  ]);
  stopWatchingRoute();
  progress.complete(t('progress.deliveredDetail'));
  hooks.onDelivered(progress);
  unsubscribe();
  channel.close();
  pc.close();
};
